import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Candy, Popcorn, Gift, XCircle, ArrowLeft, Trophy, Star, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { spinWheel } from "@/lib/api";
import Footer from "@/components/Footer";
import { track } from "@/lib/tracker";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/b1ee0792-8313-479c-984a-b18d82c4310c/images/15868f79d17e434f5979ba799d3e2c1edf01f6e606e47f5a2e6f151e60fe1f60.png";

// Visual wheel layout — must match backend WHEEL_SEGMENTS order
// Colors drawn from site palette: deep night, gold, mystic
const SEGMENTS = [
  { key: "niks", label: "NIKS", color: "#160F24", textColor: "#B5A9C9", icon: XCircle },
  { key: "popcorn", label: "POPCORN", color: "#E5B942", textColor: "#0B0716", icon: Popcorn },
  { key: "niks", label: "NIKS", color: "#1F1435", textColor: "#B5A9C9", icon: XCircle },
  { key: "suikerspin", label: "SUIKERSPIN", color: "#5A2FCC", textColor: "#F8F5FF", icon: Candy },
  { key: "niks", label: "NIKS", color: "#160F24", textColor: "#B5A9C9", icon: XCircle },
  { key: "jackpot", label: "JACKPOT", color: "#F4D068", textColor: "#0B0716", icon: Trophy },
  { key: "niks", label: "NIKS", color: "#1F1435", textColor: "#B5A9C9", icon: XCircle },
  { key: "popcorn", label: "POPCORN", color: "#E5B942", textColor: "#0B0716", icon: Popcorn },
  { key: "niks", label: "NIKS", color: "#160F24", textColor: "#B5A9C9", icon: XCircle },
  { key: "suikerspin", label: "SUIKERSPIN", color: "#5A2FCC", textColor: "#F8F5FF", icon: Candy },
];

const PRIZE_MESSAGES = {
  niks: { title: "Niks gewonnen", subtitle: "Geen prijs deze keer. Kom later nog eens draaien!", icon: XCircle, color: "text-[#B5A9C9]" },
  popcorn: { title: "Gratis popcorn! 🍿", subtitle: "Toon dit scherm aan Seppe om jouw gratis popcorn af te halen.", icon: Popcorn, color: "text-gold" },
  suikerspin: { title: "Gratis suikerspin! 🍭", subtitle: "Toon dit scherm aan Seppe om jouw gratis suikerspin af te halen.", icon: Candy, color: "text-pink-400" },
  jackpot: { title: "JACKPOT! 🎉", subtitle: "Wauw, een verrassingsprijs! Toon dit scherm aan Seppe.", icon: Trophy, color: "text-mystic-light" },
};

const SEGMENT_ANGLE = 360 / SEGMENTS.length;

function getSessionId() {
  try {
    let sid = localStorage.getItem("ms_sid");
    if (!sid) {
      sid = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2);
      localStorage.setItem("ms_sid", sid);
    }
    return sid;
  } catch {
    return "anon-" + Math.random().toString(36).slice(2);
  }
}

function getDevice() {
  const ua = navigator.userAgent || "";
  return /Mobi|Android|iPhone|iPad|iPod/i.test(ua) ? "mobile" : "desktop";
}

export default function Rad() {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null); // { prize, segment_index }
  const [alreadySpun, setAlreadySpun] = useState(false);
  const wheelRef = useRef(null);

  useEffect(() => {
    track("visit");
    try {
      const last = localStorage.getItem("ms_rad_last");
      if (last) {
        const today = new Date().toISOString().slice(0, 10);
        if (last.slice(0, 10) === today) {
          setAlreadySpun(true);
        }
      }
    } catch { /* ignore */ }
  }, []);

  const handleSpin = async () => {
    if (spinning || result) return;
    setSpinning(true);
    try {
      const res = await spinWheel(getSessionId(), getDevice());
      // Calculate target rotation: pointer at top, segment 0 starts at angle 0.
      // Segment i center is at (i + 0.5) * SEGMENT_ANGLE.
      // We rotate CCW so target rotation lands segment i center under the top pointer.
      const targetSegmentCenter = res.segment_index * SEGMENT_ANGLE + SEGMENT_ANGLE / 2;
      // Add 6 full rotations for drama; end so that segment center is at top (0deg)
      const finalRotation = 360 * 6 + (360 - targetSegmentCenter);
      setRotation(finalRotation);

      // After spin animation ends, show result
      setTimeout(() => {
        setResult(res);
        setSpinning(false);
        try {
          localStorage.setItem("ms_rad_last", new Date().toISOString());
        } catch { /* ignore */ }
      }, 5200);
    } catch (err) {
      setSpinning(false);
      if (err.response?.status === 429) {
        setAlreadySpun(true);
        toast.error("Je hebt vandaag al gedraaid.");
      } else {
        toast.error("Er ging iets mis. Probeer opnieuw.");
      }
    }
  };

  const canSpin = !spinning && !result && !alreadySpun;

  return (
    <div className="min-h-screen bg-night text-[#F8F5FF] font-body overflow-x-hidden flex flex-col relative" data-testid="rad-page">
      {/* Magical background layers */}
      <div
        className="fixed inset-0 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      <div className="fixed inset-0 bg-gradient-to-b from-night/80 via-night/70 to-night pointer-events-none" />
      <div className="fixed inset-0 star-field opacity-50 pointer-events-none" />

      {/* Floating decorative sparkles */}
      <Sparkles className="fixed top-32 left-8 sm:left-16 w-6 h-6 text-gold/70 float twinkle pointer-events-none z-10" />
      <Sparkles className="fixed top-48 right-10 sm:right-24 w-5 h-5 text-mystic-light twinkle-delay-1 pointer-events-none z-10" />
      <Star className="fixed top-72 left-[12%] w-4 h-4 text-gold/50 fill-gold/40 twinkle-delay-2 pointer-events-none z-10 hidden sm:block" />
      <Sparkles className="fixed bottom-48 left-[8%] w-5 h-5 text-gold/60 twinkle pointer-events-none z-10 hidden sm:block" />
      <Sparkles className="fixed bottom-60 right-[10%] w-6 h-6 text-mystic float twinkle-delay-2 pointer-events-none z-10" />
      <Star className="fixed top-[45%] right-8 w-4 h-4 text-gold/60 fill-gold/40 twinkle-delay-1 pointer-events-none z-10 hidden sm:block" />
      <Star className="fixed top-[55%] left-6 w-3 h-3 text-mystic-light/60 fill-mystic/40 twinkle pointer-events-none z-10 hidden sm:block" />
      <Wand2 className="fixed bottom-32 right-[15%] w-5 h-5 text-gold/50 twinkle-delay-1 pointer-events-none z-10 hidden md:block" />

      <header className="fixed top-0 left-0 right-0 z-40 glass-strong py-4">
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-2 group" data-testid="rad-nav-logo">
            <Sparkles className="w-6 h-6 text-gold group-hover:rotate-12 transition-transform duration-300" />
            <span className="font-display text-xl sm:text-2xl font-bold tracking-tight">
              Magic <span className="gold-text">Seppe</span>
            </span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#B5A9C9] hover:text-gold" data-testid="rad-back">
            <ArrowLeft className="w-4 h-4" />
            Terug
          </Link>
        </nav>
      </header>

      <main className="flex-1 pt-32 pb-16 px-6 lg:px-10 relative z-10">
        <div className="relative max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-6">
            <Gift className="w-4 h-4 text-gold" />
            <span className="text-xs uppercase tracking-widest text-[#B5A9C9]">Magisch gelukswiel</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05]">
            Draai het <span className="gold-text italic">rad</span>.
          </h1>
          <p className="mt-6 text-lg text-[#B5A9C9] max-w-xl mx-auto">
            Draai 1 keer per dag en maak kans op popcorn, suikerspin of de mysterieuze jackpot.
          </p>

          {/* Wheel */}
          <div className="relative mx-auto mt-12 mb-10" style={{ width: "min(90vw, 480px)", aspectRatio: "1 / 1" }}>
            {/* Outer glow ring */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gold/40 via-mystic/30 to-gold/20 blur-2xl animate-pulse" />
            {/* Star ring around wheel */}
            <div className="absolute -inset-8 pointer-events-none">
              {[...Array(12)].map((_, i) => {
                const angle = (i / 12) * 2 * Math.PI;
                const x = 50 + 50 * Math.cos(angle);
                const y = 50 + 50 * Math.sin(angle);
                const delayClass = i % 3 === 0 ? "twinkle" : i % 3 === 1 ? "twinkle-delay-1" : "twinkle-delay-2";
                return (
                  <Sparkles
                    key={i}
                    className={`absolute w-4 h-4 text-gold ${delayClass}`}
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                );
              })}
            </div>
            {/* Outer decorative ring */}
            <div className="absolute inset-0 rounded-full border-4 border-gold/60 gold-glow" />

            {/* Wheel SVG */}
            <div
              ref={wheelRef}
              className="absolute inset-2 rounded-full overflow-hidden"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: spinning ? "transform 5s cubic-bezier(0.17, 0.67, 0.16, 1)" : "none",
              }}
              data-testid="rad-wheel"
            >
              <svg viewBox="-100 -100 200 200" className="w-full h-full">
                <defs>
                  <filter id="star-glow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="0.6" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <radialGradient id="star-dot" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#F8F5FF" stopOpacity="1" />
                    <stop offset="50%" stopColor="#F4D068" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#E5B942" stopOpacity="0" />
                  </radialGradient>
                </defs>
                {SEGMENTS.map((seg, i) => {
                  const startAngle = i * SEGMENT_ANGLE;
                  const endAngle = (i + 1) * SEGMENT_ANGLE;
                  const startRad = ((startAngle - 90) * Math.PI) / 180;
                  const endRad = ((endAngle - 90) * Math.PI) / 180;
                  const r = 100;
                  const x1 = r * Math.cos(startRad);
                  const y1 = r * Math.sin(startRad);
                  const x2 = r * Math.cos(endRad);
                  const y2 = r * Math.sin(endRad);
                  const largeArc = SEGMENT_ANGLE > 180 ? 1 : 0;

                  const midAngle = startAngle + SEGMENT_ANGLE / 2;
                  const textRad = ((midAngle - 90) * Math.PI) / 180;
                  const textR = 62;
                  const tx = textR * Math.cos(textRad);
                  const ty = textR * Math.sin(textRad);

                  // 5-point star path
                  const fivePointStar = "M 0 -4 L 1.18 -1.24 L 4.05 -1.24 L 1.71 0.47 L 2.59 3.24 L 0 1.5 L -2.59 3.24 L -1.71 0.47 L -4.05 -1.24 L -1.18 -1.24 Z";
                  const isDarkSeg = seg.key === "niks";
                  const starColor = isDarkSeg ? "#F4D068" : (seg.color === "#E5B942" || seg.color === "#F4D068") ? "#7A5A15" : "#F4D068";
                  const dotColor = isDarkSeg ? "#F8F5FF" : (seg.color === "#E5B942" || seg.color === "#F4D068") ? "#7A5A15" : "#F8F5FF";

                  // Deterministic pseudo-random positions per segment for scattered dots
                  const seed = i * 31;
                  const dots = [
                    { r: 42, offset: 0.15, size: 0.7 },
                    { r: 55, offset: -0.3, size: 0.5 },
                    { r: 72, offset: 0.25, size: 0.8 },
                    { r: 78, offset: -0.1, size: 0.5 },
                    { r: 90, offset: 0.05, size: 0.6 },
                    { r: 35, offset: 0.35, size: 0.4 },
                    { r: 65, offset: -0.32, size: 0.5 },
                  ];

                  return (
                    <g key={i}>
                      <path
                        d={`M 0 0 L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={seg.color}
                        stroke="#0B0716"
                        strokeWidth="0.8"
                      />

                      {/* Scattered small "distant" stars */}
                      {dots.map((d, di) => {
                        const a = ((midAngle + d.offset * SEGMENT_ANGLE - 90) * Math.PI) / 180;
                        const dx = d.r * Math.cos(a);
                        const dy = d.r * Math.sin(a);
                        const opacity = 0.4 + ((seed + di) % 5) * 0.1;
                        return (
                          <circle
                            key={di}
                            cx={dx}
                            cy={dy}
                            r={d.size}
                            fill={dotColor}
                            opacity={opacity}
                          />
                        );
                      })}

                      {/* Larger glowing 5-point star near outer edge */}
                      <g
                        transform={`translate(${85 * Math.cos(((midAngle - 90) * Math.PI) / 180)} ${85 * Math.sin(((midAngle - 90) * Math.PI) / 180)}) rotate(${midAngle})`}
                        filter="url(#star-glow)"
                      >
                        <path d={fivePointStar} fill={starColor} opacity="0.9" />
                      </g>

                      {/* Medium star inner */}
                      <g
                        transform={`translate(${32 * Math.cos(((midAngle - 90) * Math.PI) / 180)} ${32 * Math.sin(((midAngle - 90) * Math.PI) / 180)}) rotate(${midAngle * 1.2}) scale(0.55)`}
                        filter="url(#star-glow)"
                      >
                        <path d={fivePointStar} fill={starColor} opacity="0.7" />
                      </g>

                      {/* Sparkle highlight dot with radial gradient */}
                      <circle
                        cx={48 * Math.cos(((midAngle + 0.4 * SEGMENT_ANGLE - 90) * Math.PI) / 180)}
                        cy={48 * Math.sin(((midAngle + 0.4 * SEGMENT_ANGLE - 90) * Math.PI) / 180)}
                        r="2.5"
                        fill="url(#star-dot)"
                      />

                      <g transform={`translate(${tx} ${ty}) rotate(${midAngle + 90})`}>
                        <text
                          x="0"
                          y="2"
                          textAnchor="middle"
                          fill={seg.textColor}
                          fontSize="9"
                          fontWeight="800"
                          style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "1px" }}
                        >
                          {seg.label}
                        </text>
                      </g>
                    </g>
                  );
                })}
                {/* Center circle */}
                <circle cx="0" cy="0" r="12" fill="#0B0716" stroke="#E5B942" strokeWidth="2" />
                <circle cx="0" cy="0" r="4" fill="#E5B942" />
              </svg>
            </div>

            {/* Pointer */}
            <div className="absolute left-1/2 -top-2 -translate-x-1/2 z-10">
              <svg width="40" height="50" viewBox="0 0 40 50">
                <polygon points="20,45 5,5 35,5" fill="#E5B942" stroke="#0B0716" strokeWidth="2" />
                <circle cx="20" cy="10" r="4" fill="#0B0716" />
              </svg>
            </div>
          </div>

          {/* Actions */}
          {!result ? (
            <Button
              onClick={handleSpin}
              disabled={!canSpin}
              className="px-10 py-6 rounded-full bg-gold text-night-deep hover:bg-gold-light font-semibold text-lg gold-glow-hover disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="rad-spin-button"
            >
              {alreadySpun && !spinning
                ? "Al gedraaid vandaag"
                : spinning
                ? "Aan het draaien..."
                : "DRAAI HET RAD"}
            </Button>
          ) : (
            <ResultCard result={result} />
          )}

          <p className="mt-8 text-xs text-[#7A6C96]">
            {alreadySpun ? "Je hebt vandaag al gedraaid." : "Eén draai per dag. Winnende prijzen kunnen worden afgehaald bij Magic Seppe."}
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function ResultCard({ result }) {
  const msg = PRIZE_MESSAGES[result.prize] || PRIZE_MESSAGES.niks;
  const Icon = msg.icon;
  const isWinner = result.prize !== "niks";

  return (
    <div
      className={`mx-auto max-w-md rounded-3xl p-8 glass-strong ${isWinner ? "gold-glow" : ""}`}
      data-testid="rad-result"
    >
      <Icon className={`w-16 h-16 mx-auto mb-4 ${msg.color}`} />
      <h2 className="font-display text-3xl font-bold mb-3">{msg.title}</h2>
      <p className="text-[#B5A9C9]">{msg.subtitle}</p>
      {isWinner && (
        <div className="mt-6 pt-6 border-t border-gold/20">
          <p className="text-xs uppercase tracking-widest text-gold">Belangrijk</p>
          <p className="text-sm text-[#B5A9C9] mt-2">
            Ververs deze pagina NIET — toon dit scherm aan Seppe om je prijs op te halen.
          </p>
        </div>
      )}
    </div>
  );
}
